// src/services/AuthService.js

import { initializeApp } from 'firebase/app';
import {
    getAuth,
    signInAnonymously,
    onAuthStateChanged,
    setPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup,
    signOut
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getAnalytics } from 'firebase/analytics';

import { firebaseConfig } from '../configs/firebaseConfig';

// Firebaseサービスのインスタンスを保持する変数
let app = null;
let db = null;
let auth = null;
let analytics = null;

/**
 * Firebaseを初期化し、認証サービスを設定する
 * @param {Function} onAuthChangeCallback - 認証状態が変更されたときに呼び出されるコールバック
 * @returns {Object} 初期化されたFirebaseサービス (db, auth)
 * @throws {Error} Firebase設定が不足している場合
 */
export const initializeAndMonitorAuth = async (onAuthChangeCallback) => {
    if (app) { // 既に初期化されている場合は再初期化しない
        console.warn("Firebaseは既に初期化されています。");
        // 既存の認証状態をすぐにコールバックに通知
        if (auth && auth.currentUser) {
            onAuthChangeCallback(auth.currentUser);
        } else {
            onAuthChangeCallback(null);
        }
        return { db, auth };
    }

    // Firebase設定が正しく入力されているかチェック
    if (firebaseConfig.apiKey === "YOUR_FIREBASE_API_KEY_HERE" || !firebaseConfig.projectId) {
        throw new Error("Firebase設定がApp.jsまたはfirebaseConfig.jsに設定されていません。実際のFirebaseプロジェクト設定で更新してください。");
    }

    try {
        app = initializeApp(firebaseConfig);
        db = getFirestore(app);
        auth = getAuth(app);
        analytics = getAnalytics(app); // Analyticsの初期化を実行
        // analytics変数が直接使用されていなくても、getAnalyticsの呼び出し自体は意味があるため、
        // この警告は通常、無視するか、将来的な使用を意図しているものとして扱われます。
        // ESLintの警告を抑制したい場合は、次の行に // eslint-disable-next-line no-unused-vars を追加できます。

        // onAuthStateChanged は認証状態の変更をリッスンし、
        // 初回ロード時にも現在のユーザー情報を取得する
        onAuthStateChanged(auth, (user) => {
            onAuthChangeCallback(user);
        });

        return { db, auth };
    } catch (err) {
        console.error("Firebase初期化に失敗しました:", err);
        throw err; // エラーを呼び出し元に再スロー
    }
};

/**
 * メールアドレスとパスワードで新しいユーザーを作成する
 * @param {string} email - ユーザーのメールアドレス
 * @param {string} password - ユーザーのパスワード
 * @returns {Promise<UserCredential>}
 */
export const signUpWithEmail = async (email, password) => {
    if (!auth) throw new Error("Firebase Authが初期化されていません。");
    // メール/パスワード認証はデフォルトでbrowserLocalPersistence（永続的）
    await setPersistence(auth, browserLocalPersistence);
    return await createUserWithEmailAndPassword(auth, email, password);
};

/**
 * メールアドレスとパスワードでサインインする
 * @param {string} email - ユーザーのメールアドレス
 * @param {string} password - ユーザーのパスワード
 * @returns {Promise<UserCredential>}
 */
export const signInWithEmail = async (email, password) => {
    if (!auth) throw new Error("Firebase Authが初期化されていません。");
    // メール/パスワード認証はデフォルトでbrowserLocalPersistence（永続的）
    await setPersistence(auth, browserLocalPersistence);
    return await signInWithEmailAndPassword(auth, email, password);
};

/**
 * Googleアカウントでサインインする
 * @returns {Promise<UserCredential>}
 */
export const signInWithGoogle = async () => {
    if (!auth) throw new Error("Firebase Authが初期化されていません。");
    const provider = new GoogleAuthProvider();
    // Google認証もデフォルトでbrowserLocalPersistence（永続的）
    await setPersistence(auth, browserLocalPersistence);
    // Googleアカウント選択プロンプトを常に表示するように設定
    provider.setCustomParameters({
        prompt: 'select_account' // これにより、毎回アカウント選択画面が表示されます
    });
    return await signInWithPopup(auth, provider);
};

/**
 * ゲスト（匿名）としてサインインする
 * セッションベースで、ブラウザのリロードでユーザーIDが変わる
 * @returns {Promise<UserCredential>}
 */
export const signInAsGuest = async () => {
    if (!auth) throw new Error("Firebase Authが初期化されていません。");
    // 匿名認証はセッションごとに再認証（永続化しない）
    await setPersistence(auth, browserSessionPersistence);
    return await signInAnonymously(auth);
};

/**
 * 現在のユーザーをログアウトさせる
 * @returns {Promise<void>}
 */
export const logout = async () => {
    if (!auth) throw new Error("Firebase Authが初期化されていません。");
    return await signOut(auth);
};

// 外部からFirestoreとAuthインスタンスにアクセスするためのgetter
// これらの関数はApp.jsでは直接使用されなくなりましたが、
// AuthServiceがFirebaseインスタンスへのアクセスを提供するために存在します。
export const getFirebaseDb = () => db;
export const getFirebaseAuth = () => auth;
